import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { logError } from "@/lib/log";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Siren, CheckCircle2, Circle, FolderOpen, ClipboardList, PhoneCall, Sparkles, Loader2 } from "lucide-react";

export default function InspectionDay() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/me/inspection-day").then((r) => setData(r.data)).catch((err) => logError("Inspection day load failed", err));
  }, []);

  const toggle = async (index, done) => {
    const { data: r } = await api.post("/me/inspection-day/steps", { index, done });
    setData((d) => ({ ...d, done: r.done }));
  };

  if (!data) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  const doneCount = data.steps.filter((_, i) => data.done.includes(i)).length;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="inspection-day-page">
      <div className="rounded-2xl bg-destructive text-destructive-foreground p-6 mb-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <span className="text-[11px] font-bold uppercase tracking-[0.15em] opacity-90">Inspection day mode</span>
            <h1 className="font-display text-2xl sm:text-3xl font-extrabold mt-1">Stay calm. Follow the plan.</h1>
            <p className="text-sm opacity-90 mt-1">{data.label} · {doneCount}/{data.steps.length} steps done</p>
          </div>
          <Button asChild variant="secondary" data-testid="inspection-day-call">
            <a href={`tel:${data.support_phone.replace(/\s+/g, "")}`}><PhoneCall className="w-4 h-4 mr-1.5" /> Call our compliance specialist {data.support_phone}</a>
          </Button>
        </div>
      </div>

      <section className="bg-card border border-border rounded-2xl p-5 mb-6" data-testid="inspection-day-steps">
        <h2 className="font-display font-bold mb-3 flex items-center gap-2"><Siren className="w-4 h-4 text-destructive" /> On-the-day checklist</h2>
        <ul className="space-y-2">
          {data.steps.map((step, i) => {
            const done = data.done.includes(i);
            return (
              <li key={step}>
                <button type="button" onClick={() => toggle(i, !done)} data-testid={`inspection-day-step-${i}`}
                  className={`w-full text-left flex items-start gap-3 rounded-xl border p-3 transition-colors ${done ? "border-accent/40 bg-accent/10" : "border-border hover:bg-secondary"}`}>
                  {done ? <CheckCircle2 className="w-5 h-5 text-accent shrink-0 mt-0.5" /> : <Circle className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />}
                  <span className={`text-sm ${done ? "line-through text-muted-foreground" : ""}`}>{step}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </section>

      <div className="grid sm:grid-cols-2 gap-6">
        <section className="bg-card border border-border rounded-2xl p-5" data-testid="inspection-day-evidence">
          <h2 className="font-display font-bold mb-3 flex items-center gap-2"><FolderOpen className="w-4 h-4 text-primary" /> Evidence quick-links</h2>
          {data.evidence.length === 0 ? (
            <p className="text-sm text-muted-foreground">No evidence saved yet. <Link to="/evidence" className="text-primary hover:underline">Open your evidence folder</Link>.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {data.evidence.map((e) => (
                <li key={e.id} className="flex items-center justify-between gap-2">
                  <Link to="/evidence" className="truncate hover:underline">{e.title}</Link>
                  {e.key_question && <Badge variant="secondary" className="shrink-0 text-[10px]">{e.key_question}</Badge>}
                </li>
              ))}
            </ul>
          )}
          <Button asChild variant="outline" size="sm" className="mt-4"><Link to="/evidence">Open evidence folder</Link></Button>
        </section>

        <section className="bg-card border border-border rounded-2xl p-5" data-testid="inspection-day-actions">
          <h2 className="font-display font-bold mb-3 flex items-center gap-2"><ClipboardList className="w-4 h-4 text-primary" /> Open improvement actions</h2>
          {data.open_actions.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nothing outstanding — well done.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {data.open_actions.map((a) => (
                <li key={a.id} className="flex items-center justify-between gap-2">
                  <span className="truncate">{a.title}</span>
                  {a.due_date && <span className="text-xs text-muted-foreground shrink-0">due {a.due_date}</span>}
                </li>
              ))}
            </ul>
          )}
          <Button asChild variant="outline" size="sm" className="mt-4"><Link to="/inspections">Open improvement plan</Link></Button>
        </section>
      </div>

      <div className="mt-6 rounded-2xl border border-border bg-secondary/50 p-5 flex items-start gap-3" data-testid="inspection-day-kq">
        <Sparkles className="w-5 h-5 text-primary mt-0.5" />
        <div>
          <p className="text-sm font-semibold">The inspector will be thinking about: {data.key_questions.join(" · ")}</p>
          <p className="text-sm text-muted-foreground mt-1">Stuck on a question? <Link to="/assistant" className="text-primary hover:underline">Ask the AI assistant</Link> — it can answer in seconds while you wait.</p>
        </div>
      </div>
    </div>
  );
}
